/**
 * Validates and auto-corrects **web URLs** by adding a missing scheme and doing safe normalization.
 * Drop-in replacement for your original functions (same names/signatures).
 *
 * Notes:
 * - If no scheme is present, assumes `https://`.
 * - Accepts http(s), localhost, IPv4, IPv6, and IDNs.
 * - Validates that hostname is either localhost, an IP, or a domain with a 2+ char TLD.
 * - Only normalizes duplicate slashes in the **path** (won’t touch protocol, query, or hash).
 * - Preserves the absence of a trailing slash at the origin to better match your original output.
 */

const SCHEME_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
const IPV4_RE =
  /^(25[0-5]|2[0-4]\d|[0-1]?\d?\d)(\.(25[0-5]|2[0-4]\d|[0-1]?\d?\d)){3}$/;

function isIPv6(host: string): boolean {
  // WHATWG URL exposes bare IPv6 in `hostname` (no brackets)
  return host.includes(':');
}

function hasValidDomainTld(host: string): boolean {
  // Allow a trailing dot for FQDNs during validation
  const hostNoTrail = host.replace(/\.$/, '');
  const parts = hostNoTrail.split('.');
  if (parts.length < 2) return false;
  const tld = parts[parts.length - 1];
  return tld.length >= 2;
}

function normalizePathSlashes(pathname: string): string {
  // Collapse only multiple slashes in the PATH component
  return pathname.replace(/\/{2,}/g, '/');
}

/**
 * Validates and auto-corrects URIs by adding missing protocol (https) if needed.
 * Accepts localhost/IPs and does safe path normalization.
 */
export function validateAndCorrectUri(uri: string): string {
  if (!uri) return uri;

  let input = uri.trim().normalize('NFC');

  // If no scheme at all, assume https://
  if (!SCHEME_RE.test(input)) {
    input = 'https://' + input;
  }

  let url: URL;
  try {
    url = new URL(input);
  } catch (e: any) {
    throw new Error(`Invalid URL: ${e?.message ?? 'parse failed'}`);
  }

  // Enforce web URLs only
  if (!/^https?:$/.test(url.protocol)) {
    throw new Error(`Unsupported scheme: ${url.protocol}`);
  }

  const host = url.hostname;

  // Host must be localhost, IP, or have a valid-looking TLD
  const isHostAllowed =
    host === 'localhost' ||
    IPV4_RE.test(host) ||
    isIPv6(host) ||
    hasValidDomainTld(host);

  if (!isHostAllowed) {
    throw new Error(
      'Invalid domain structure (hostname must be localhost, IP, or have a valid TLD)'
    );
  }

  // Safe normalization: only touch the path
  url.pathname = normalizePathSlashes(url.pathname);

  // Preserve absence of trailing slash at origin if the original input lacked it.
  // The URL serializer adds "/" for bare origins; drop it iff the caller didn’t supply one.
  const serialized = url.toString();
  const inputTrimmed = uri.trim();
  const inputHasExplicitTrailingSlashAtOrigin =
    /^(?:[a-zA-Z][a-zA-Z0-9+.-]*:)?\/\/[^/?#]+\/(?:\?|#|$)/.test(inputTrimmed);

  if (url.pathname === '/' && !inputHasExplicitTrailingSlashAtOrigin) {
    // Remove the trailing "/" just before ?, #, or end
    return serialized.replace(/\/(?=\?|#|$)/, '');
  }

  return serialized;
}

/**
 * Checks if a string is a valid **web URL** with proper host structure.
 * Unlike validateAndCorrectUri, this does not add a missing scheme.
 */
export function isValidUri(uri: string): boolean {
  if (!uri) return false;

  try {
    const url = new URL(uri);

    if (!/^https?:$/.test(url.protocol)) return false;

    const host = url.hostname;
    if (
      host === 'localhost' ||
      IPV4_RE.test(host) ||
      isIPv6(host) ||
      hasValidDomainTld(host)
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
